import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import { ModeTabs } from "./ModeTabs";

type ModeTabsContextValue = {
  target: HTMLDivElement | null;
  setTarget: (target: HTMLDivElement | null) => void;
};

const ModeTabsContext = createContext<ModeTabsContextValue | null>(null);

type ModeTabsProviderProps = {
  children: ReactNode;
};

export const ModeTabsProvider = ({ children }: ModeTabsProviderProps) => {
  const [target, setTarget] = useState<HTMLDivElement | null>(null);
  const value = useMemo(() => ({ target, setTarget }), [target]);

  return (
    <ModeTabsContext.Provider value={value}>
      {children}
    </ModeTabsContext.Provider>
  );
};

type ModeTabsSlotProps = {
  className?: string;
};

export const ModeTabsSlot = ({ className }: ModeTabsSlotProps) => {
  const context = useContext(ModeTabsContext);

  if (!context) {
    throw new Error("ModeTabsSlot must be used within ModeTabsProvider");
  }

  return (
    <div
      ref={context.setTarget}
      className={classNames(className)}
      data-mode-tabs-slot
    />
  );
};

export const ModeTabsPortal = () => {
  const context = useContext(ModeTabsContext);

  if (!context) {
    throw new Error("ModeTabsPortal must be used within ModeTabsProvider");
  }

  if (!context.target) {
    return null;
  }

  return createPortal(<ModeTabs />, context.target);
};
